import { useLocation } from "react-router-dom";
import { usePost } from "../../API";
import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as survey from "@aidkitorg/types/lib/survey";
import { useAsyncEffect } from "../../Hooks/AsyncEffect";
import { Button, Card, CardBody, Icon, useToast } from "@aidkitorg/component-library";
import { getDistroBrowserIncompatibility } from "../../Util";
import { CUSTOM_UI_KEY_SEPARATOR, CustomUIRenderers, useCustomUI } from "@aidkitorg/typesheets/lib/useCustomUi";

const Distro = React.lazy(() => import("@aidkitorg/typesheets/lib/distroeditor"));

const blankRubric: survey.Rubric = {
  kind: 'Rubric',
  name: '',
  version: 0,
  description: '',
  graders: []
};

/**
 * Extract all MessageTemplateRef names and versions referenced in the rubric.
 * Returns a Map where key is template name, value is Set of versions (including undefined).
 * The version must match exactly what's in the ref to build correct composite keys.
 */
function extractReferencedTemplateNames(rubric: survey.Rubric): Map<string, Set<number | undefined>> {
  const namesAndVersions = new Map<string, Set<number | undefined>>();
  for (const grader of rubric.graders || []) {
    if (grader.kind === 'Score Model Grader') {
      if (grader.template?.name) {
        if (!namesAndVersions.has(grader.template.name)) {
          namesAndVersions.set(grader.template.name, new Set());
        }
        // Use actual version value (could be undefined) to match editor's composite key
        namesAndVersions.get(grader.template.name)?.add(grader.template.version);
      }
    }
  }
  return namesAndVersions;
}

export function RubricConfig() {
  const listRubricsRequest = usePost('/program/admin/rubrics/list');
  const [loadedRubric, setLoadedRubric] = useState<survey.Rubric | null>(null);
  const location = useLocation();

  useAsyncEffect(async () => {
    const queryParams = new URLSearchParams(location.search);
    const name = queryParams.get('name');
    const version = queryParams.get('version');

    if (name) {
      const { items: rubrics } = await listRubricsRequest({
        name,
        version: version ? parseInt(version) : undefined
      });
      const existingRubric = rubrics?.[rubrics?.length - 1];
      if (existingRubric) {
        setLoadedRubric({ ...existingRubric, kind: 'Rubric' } as survey.Rubric);
        return;
      }
    }

    setLoadedRubric(blankRubric);
  }, []);

  if (!loadedRubric) return <div>Loading...</div>;

  return <InnerRubricConfig initial={loadedRubric} />;
}

export function InnerRubricConfig(props: { initial: survey.Rubric }) {
  const [rubric, setRubric] = useState<survey.Rubric>(props.initial);
  const [latestVersion, setLatestVersion] = useState(props.initial.version);
  const [templatesLoaded, setTemplatesLoaded] = useState(false);
  const [previousRubricVariables, setPreviousRubricVariables] = useState(new Set(rubric.variables?.map(v => v.key) ?? []));
  const [needsNewEvalId, setNeedsNewEvalId] = useState(false);

  // Track which templates are currently being fetched to avoid duplicate requests
  const fetchingTemplatesRef = useRef<Set<string>>(new Set());
  // Track all known template names for validation
  const [knownTemplates, setKnownTemplates] = useState<Record<string, { version: number, description: string | undefined }>>({});

  const distroRef = useRef<React.ComponentRef<typeof Distro>>(null);

  const createRubricRequest = usePost('/program/admin/rubrics/create');
  const listRubricsRequest = usePost('/program/admin/rubrics/list');
  const getMessageTemplateRequest = usePost('/program/admin/prompts/get');
  const getMessageTemplateNamesAndVersionsRequest = usePost('/program/admin/prompts/scan');

  const { toast } = useToast();

  // Load available message template names (lightweight - no instructions)
  useAsyncEffect(async () => {
    const result = await getMessageTemplateNamesAndVersionsRequest({ latestOnly: true });
    // We're using latestOnly, so we only get the highest version for each template, hence the [0]
    // Convert from: { [name]: { version: number, description: string }[] }
    // to:           { [name]: { version: number, description: string } }
    const templateNamesToHighestVersion = Object.fromEntries(
      Object.entries(result?.nameToVersionsAndDescriptionsMap ?? {})
        .map(([name, versionsAndDescriptions]) => [name, { version: versionsAndDescriptions[0].version, description: versionsAndDescriptions[0].description }])
    );

    setKnownTemplates(templateNamesToHighestVersion);
    setTemplatesLoaded(true);
  }, []);

  // Renderers map - stable reference to avoid Distro re-renders
  const renderers = useMemo<CustomUIRenderers<survey.MessageTemplateRef, survey.EditableMessageTemplate>>(() => ({
    messageTemplates: {
      getKey: (value: survey.MessageTemplateRef) => {
        if (!value?.name) return null;
        return value.name + (value.version ? `${CUSTOM_UI_KEY_SEPARATOR}${value.version}` : '');
      },
      render: (value: survey.MessageTemplateRef, externalData?: survey.EditableMessageTemplate) => {
        if (!externalData?.instructions) return null;
        // Parse key to get display name (key format: "name:::version" or just "name")
        const displayKey = value.version ? `${value.name} (v${value.version})` : value.name;

        return (
          <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-md w-full">
            <div className="text-xs font-semibold text-green-700 mb-2 flex items-center">
              <span>Preview: {displayKey} (Read-only)</span>
              <a
                className="ml-2 text-xs display-inline-block"
                href={`/ai/config/prompts?name=${value.name}&version=${value.version}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Icon name="ArrowTopRightOnSquareIcon" type="outline" size="sm" />
              </a>
            </div>
            {knownTemplates[value.name]?.description && (
              <div className="text-xs text-green-700 mb-2">
                <span className="font-semibold">Description: </span>{knownTemplates[value.name].description}
              </div>
            )}
            <div className="max-h-64 overflow-y-auto space-y-2">
              {externalData.instructions.map((instruction: survey.Instruction, idx: number) => {
                if (instruction.kind === 'Text Instruction') {
                  return (
                    <div key={idx} className="text-xs">
                      <span className="font-semibold">{instruction.role}: </span>
                      <span className="whitespace-pre-wrap">
                        {JSON.stringify(instruction.content, null, 2)}
                      </span>
                    </div>
                  )}
                return null;
              })}
            </div>
          </div>
        );
      }
    }
  }), [knownTemplates]);

  // Custom UI for rendering message template previews
  const { customUI, updateData } = useCustomUI<survey.MessageTemplateRef, survey.EditableMessageTemplate>(renderers);

  // Fetch template content on-demand when referenced in rubric
  // Key format must match getKey in the renderers map above
  const fetchTemplateIfNeeded = useCallback(async (templateName: string, version?: number) => {
    const compositeKey = `${templateName}${version ? `${CUSTOM_UI_KEY_SEPARATOR}${version}` : ''}`;

    // Skip if already have preview, already fetching, or not a known template
    if (customUI.getData?.('messageTemplates', compositeKey) ||
        fetchingTemplatesRef.current.has(compositeKey) ||
        !knownTemplates[templateName]) {
      return;
    }

    // Validate version exists if specified
    if (version !== undefined && knownTemplates[templateName]?.version < version) {
      console.warn(`Template ${templateName} version ${version} not found (latest is ${knownTemplates[templateName]?.version})`);
      return;
    }

    fetchingTemplatesRef.current.add(compositeKey);

    try {
      const template = await getMessageTemplateRequest({ name: templateName, version });

      if (template?.instructions) {
        const previewData = {
          kind: 'Message Template',
          name: templateName,
          version: version ?? 0,
          description: template.description,
          instructions: template.instructions
            .filter((inst: survey.Instruction) => inst.kind === 'Text Instruction')
            .map((inst: survey.TextInstruction) => ({
              kind: 'Text Instruction',
              role: inst.role || 'user',
              content: JSON.stringify(inst.content)
            } satisfies survey.TextInstruction))
        } satisfies survey.EditableMessageTemplate;

        // Store and notify subscribers via the hook
        updateData('messageTemplates', compositeKey, previewData);
      }
    } catch (error) {
      console.error(`Failed to fetch template ${templateName} v${version}:`, error);
    } finally {
      fetchingTemplatesRef.current.delete(compositeKey);
    }
  }, [knownTemplates, getMessageTemplateRequest, customUI, updateData]);

  // Watch for template references in the rubric and fetch them on-demand
  useEffect(() => {
    const referencedNamesAndVersions = extractReferencedTemplateNames(rubric);
    Array.from(referencedNamesAndVersions).forEach(([name, versions]) => {
      versions.forEach(version => fetchTemplateIfNeeded(name, version))
    });
  }, [rubric, fetchTemplateIfNeeded]);

  // Track latest version when rubric name changes
  useAsyncEffect(async () => {
    const name = rubric.name?.trim();
    if (!name) {
      setLatestVersion(0);
      return;
    }

    const result = await listRubricsRequest({ name });
    const latest = (result.items ?? [])
      .map((item: { version: number }) => item.version)
      .filter((v: unknown) => typeof v === 'number')
      .sort((a: number, b: number) => b - a)[0] ?? 0;

    setLatestVersion(latest);
  }, [rubric.name]);

  const saveRubric = useCallback(async () => {
    if (!rubric.name?.trim()) {
      toast({
        description: 'Please enter a rubric name',
        autoClose: true,
        duration: 2000
      });
      return;
    }

    const oldVersion = rubric.version ?? 0;
    const newVersion = (latestVersion ?? oldVersion) + 1;

    // Note: The backend will create/update the OpenAI Eval and set evalId
    const result = await createRubricRequest({
      rubric: { ...rubric, version: newVersion },
      needsNewEvalId: needsNewEvalId,  
    });

    if (result.success) {
      // Update local state with the new evalId from OpenAI
      const updatedRubric = { ...rubric, version: newVersion };
      setRubric(updatedRubric);
      setLatestVersion(newVersion);
      setNeedsNewEvalId(false);
      toast({
        description: oldVersion > 0
          ? `Updated rubric: v${oldVersion} → v${newVersion}`
          : 'Created new rubric!',
        autoClose: true,
        duration: 1500
      });
    }
  }, [createRubricRequest, rubric, latestVersion, toast]);

  const incompatible = getDistroBrowserIncompatibility();
  if (incompatible) return incompatible;

  const onDistroChangeHandler = useCallback((v: survey.Rubric) => {
    const newRubricVariables = v.variables?.map(v => v.key) ?? [];
    if (newRubricVariables.length !== previousRubricVariables.size || !newRubricVariables.every(v => previousRubricVariables.has(v))) {
      setNeedsNewEvalId(true);
      setPreviousRubricVariables(new Set(newRubricVariables));
    } else {
      setNeedsNewEvalId(false);
    }
    setRubric(v);
  }, [previousRubricVariables]);

  return (
    <div className="bg-gray-100 h-full p-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold">Rubric Configuration</h1>
      </div>

      <div className={`grid gap-4 grid-cols-1`}>
        <div className="bg-white shadow ring-1 ring-black ring-opacity-5 md:rounded-lg space-y-4 p-4">
          <h2 className="text-lg font-semibold">Rubric Editor</h2>

          <Card className="m-2 bg-blue-50">
            <CardBody>
              {needsNewEvalId
                ? <p>Rubric variables have changed. Please save the rubric to update the OpenAI Eval.</p>
                : rubric.evalId
                  ? <p>OpenAI Eval ID: {rubric.evalId}</p>
                  : <p>No OpenAI Eval ID yet. Save to generate one.</p>
              }
            </CardBody>
          </Card>
          {templatesLoaded ? (
            <Suspense fallback={<div>Loading editor...</div>}>
              <Distro
                ref={distroRef}
                value={rubric}
                types='src/survey.ts'
                name='Rubric'
                onChange={onDistroChangeHandler}
                customUI={customUI}
              />
            </Suspense>
          ) : (
            <div>Loading templates...</div>
          )}

          <div className='flex flex-col items-center gap-8'>
            <div className='flex flex-row gap-4'>
              <Button
                className="ml-2 mb-2"
                variant="primary"
                onClick={saveRubric}
                isDisabled={!rubric.name?.trim()}
              >
                {`Save Rubric (v${(latestVersion ?? rubric.version) + 1})`}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
